const WD = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const pad = (n) => String(n).padStart(2, "0");

const eur = (v) => `${Number(v || 0).toFixed(2).replace(".", ",")} €`;
// Zeit als HH:MM (z. B. 6.5 h -> "06:30")
const hhmm = (v) => {
  const n = Number(v || 0);
  const sign = n < 0 ? "-" : "";
  const mins = Math.round(Math.abs(n) * 60);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${sign}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

export function SummaryCards({ s }) {
  if (!s) return null;
  const overLimit = s.limit > 0 && s.counted >= s.limit;
  const items = [
    { label: "Geleistete Arbeitsstunden", value: hhmm(s.worked), testid: "sum-worked" },
    { label: "Übertrag aus Vormonat", value: hhmm(s.carry_in), testid: "sum-carryin" },
    { label: "Monatliches Limit", value: s.limit > 0 ? hhmm(s.limit) : "—", testid: "sum-limit" },
    { label: "Anrechenbare Stunden", value: hhmm(s.counted), testid: "sum-counted", accent: overLimit },
    { label: "Übertrag in Folgemonat", value: hhmm(s.overtime), testid: "sum-overtime", warn: s.overtime > 0 },
    { label: "Urlaub (Std.)", value: hhmm(s.vacation), testid: "sum-vacation" },
    { label: "Krank (Std.)", value: hhmm(s.sick), testid: "sum-sick" },
    { label: "Verdienst", value: eur(s.earnings), testid: "sum-earnings", strong: true },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8" data-testid="timesheet-summary">
      {items.map((it) => (
        <div
          key={it.label}
          className={`rounded-md border p-4 ${it.strong ? "border-yellow bg-yellow/10" : "border-light-gray bg-white"}`}
          data-testid={it.testid}
        >
          <p className={`text-xl font-bold ${it.warn ? "text-red-600" : "text-anthracite"}`}>{it.value}</p>
          <p className="mt-1 text-xs text-mid-gray">{it.label}</p>
        </div>
      ))}
    </div>
  );
}

export function CalendarGrid({ year, month, entriesByDate, onDayClick }) {
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7; // Mo=0
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="mt-6 rounded-md border border-light-gray bg-white p-3 sm:p-4" data-testid="timesheet-calendar">
      <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
        {WD.map((w, i) => (
          <div key={w} className={`pb-1 text-center text-xs font-bold uppercase tracking-wider ${i >= 5 ? "text-mid-gray/60" : "text-mid-gray"}`}>
            {w}
          </div>
        ))}
        {cells.map((d, idx) => {
          if (d === null) return <div key={`b${idx}`} />;
          const dateStr = `${year}-${pad(month)}-${pad(d)}`;
          const entries = entriesByDate[dateStr] || [];
          const total = entries.reduce((a, e) => a + (e.hours || 0), 0);
          const weekday = (new Date(year, month - 1, d).getDay() + 6) % 7;
          const isWeekend = weekday >= 5;
          const isToday = dateStr === todayStr;
          return (
            <button
              key={dateStr}
              onClick={() => onDayClick && onDayClick(dateStr)}
              disabled={!onDayClick}
              className={`flex min-h-[64px] flex-col rounded-sm border p-1.5 text-left transition-colors sm:min-h-[84px]
                ${isWeekend ? "bg-light-gray/60" : "bg-white"}
                ${isToday ? "border-yellow" : "border-light-gray"}
                ${onDayClick ? "hover:border-yellow hover:bg-yellow/5 cursor-pointer" : "cursor-default"}`}
              data-testid={`cal-day-${dateStr}`}
            >
              <span className={`text-xs font-bold ${isWeekend ? "text-mid-gray" : "text-anthracite"}`}>{d}</span>
              {entries.length > 0 && (
                <span className="mt-auto space-y-0.5">
                  {entries.slice(0, 2).map((e) => {
                    const kind = e.kind || "work";
                    const bg = kind === "vacation" ? "bg-orange-200"
                      : kind === "sick" ? "bg-blue-200"
                      : "bg-yellow/80";
                    return (
                      <span key={e.id} className={`block truncate rounded-[3px] ${bg} px-1 text-[10px] font-semibold text-brand-black`}>
                        {kind === "vacation" ? "Urlaub" : kind === "sick" ? "Krank" : `${e.start}–${e.end}`}
                      </span>
                    );
                  })}
                  {entries.length > 2 && <span className="block text-[10px] text-mid-gray">+{entries.length - 2}</span>}
                  <span className="block text-[10px] font-bold text-anthracite">{hhmm(total)}</span>
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
